import { Injectable } from '@angular/core';
import { DeeplinkService } from '@core/services/core/deeplink.service';
import { LogService } from '@core/services/core/log.service';
import { NotificationsHelperService } from '@core/services/core/notifications/notifications-helper.service';
import { NotificationsReceiverService } from '@core/services/core/notifications/notifications-receiver.service';
import { FirebaseX } from '@ionic-native/firebase-x/ngx';
import { LocalNotifications } from '@ionic-native/local-notifications/ngx';

@Injectable({
  providedIn: 'root'
})
export class NotificationsCoreService {

  constructor(
    private firebaseX: FirebaseX,
    private log: LogService,
    private deeplinks: DeeplinkService,
    private localNotifications: LocalNotifications,
    private notificationsReceiver: NotificationsReceiverService,
    private notificationsHelperService: NotificationsHelperService
  ) {}

  async init() {
    await this.setPushListeners();
    this.notificationsHelperService.setDefaulSFMCNotificationsPreference();
    this.setLocalNotificationListeners();
  }

  private setLocalNotificationListeners() {
    this.localNotifications
      .on('click')
      .subscribe(message => this.onLocalNotificationTapped(message));
    // https://github.com/katzer/cordova-plugin-local-notifications/blob/caff55ec758fdf298029ae98aff7f6a8a097feac/README.md#launch-details
    this.localNotifications.fireQueuedEvents();
  }

  private async setPushListeners() {
    // Firebase
    await this.grantFirebasePermission();
    this.setFirebaseListener();
    // SFMC
    this.notificationsReceiver.registerSFMCNotificationListeners();
  }

  private async setFirebaseListener() {
    this.firebaseX
      .onMessageReceived()
      .subscribe(message => this.processNotification(message));
    this.firebaseX
      .getToken()
      .then(token => {
        this.log.i(this, 'FCM token is', token)
      });
  }

  private onLocalNotificationTapped(msg) {
    if (msg.data.pushMessage) {
      // As we are forwarding push received in foreground, this completes the cycle.
      // No other local notification use atm. Will be needed to extend to support local
      // notifications not launched from a push message
      msg.data.pushMessage.tap = true;
      this.processNotification(msg.data.pushMessage);
    }
  }

  private processNotification(msg) {
    this.log.d(this, `FCM message: ${msg}`);
    if (msg.messageType === 'notification') {
      this.log.d(this, 'Notification message received');
      if (msg.tap) {
        this.log.d(this, `Tapped in  ${msg.tap}. Searching action`);
        if (msg.deeplink) {
          this.log.d(this, `Deeplink found:  ${msg.deeplink}`);
          this.deeplinks.processDeepLink({ $link: { path: msg.deeplink } });
        }
      } else {
        // Message received on foreground, not tapped, show it as local notification if it isn't already
        if (this.isLocalNotification(msg)) return;

        const msgTitle =
          (msg && msg.title ? msg.title : null) ||
          (msg && msg.aps && msg.aps.alert && msg.aps.alert.title
            ? msg.aps.alert.title
            : null);
        const msgBody =
          (msg && msg.body ? msg.body : null) ||
          (msg && msg.aps && msg.aps.alert && msg.aps.alert.body
            ? msg.aps.alert.body
            : null);
        this.localNotifications.schedule({
          title: msgTitle,
          text: msgBody,
          foreground: true,
          icon: 'notification_icon',
          smallIcon: 'notification_icon',
          color: 'D81E05',
          data: {
            pushMessage: msg
          }
        });
      }
    }
  }

  private isLocalNotification(msg) {
    return msg.meta && msg.meta.plugin === 'cordova-plugin-local-notification';
  }

  private async grantFirebasePermission() {
    try {
      const hasPermission = await this.firebaseX.hasPermission();
      if (!hasPermission) {
        const granted = await this.firebaseX.grantPermission();
        this.log.i(
          this,
          `Push permission was ${granted ? 'granted' : 'denied'}`
        );
      }
    } catch (err) {
      this.log.e(this, `Error tryng to grant firebase permission `, err);
    }
  }
}
